import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { kathaApi, homeApi } from '../api/api';
import { youtubeThumb } from '../utils/youtube';

import {
  buildCategoryFilters,
  getCategoryMeta,
} from '../data/kathaData';
import SectionHeading from '../components/katha/SectionHeading';
import CategoryFilters from '../components/katha/CategoryFilters';
import MediaCard from '../components/katha/MediaCard';
import VideoModal from '../components/katha/VideoModal';
import SocialLink from '../components/ui/SocialLink';
import { DEFAULT_SOCIAL_URLS, openSocialUrl } from '../utils/socialLinks';

const DEFAULT_YT_URL = DEFAULT_SOCIAL_URLS.youtube;
const DEFAULT_YT_VALUES = ['25K+', '150+', '2M+'];



const Katha = () => {
  const { t } = useTranslation();
  const [videoFilter, setVideoFilter] = useState('all');
  const [playlistFilter, setPlaylistFilter] = useState('all');
  const [playingVideo, setPlayingVideo] = useState(null);

  // API-fetched data — replaces static topVideosData and playlistsData
  const [topVideosData, setTopVideosData] = useState([]);
  const [playlistsData, setPlaylistsData] = useState([]);
  const [, setKathaLoading] = useState(true);
  const [kathaLoaded, setKathaLoaded] = useState(false);
  const [, setKathaError] = useState('');
  const [ytStats, setYtStats] = useState(() => [
    { value: DEFAULT_YT_VALUES[0], labelKey: 'kathaPage.stat_subscribers' },
    { value: DEFAULT_YT_VALUES[1], labelKey: 'kathaPage.stat_videos' },
    { value: DEFAULT_YT_VALUES[2], labelKey: 'kathaPage.stat_views' },
  ]);
  const [ytUrl, setYtUrl] = useState(DEFAULT_YT_URL);

  useEffect(() => {
    setKathaLoading(true);
    Promise.all([
      kathaApi.getAll({ type: 'video' }),
      kathaApi.getAll({ type: 'playlist' }),
    ]).then(([vRes, pRes]) => {
      if (vRes.success) {
        setTopVideosData((vRes.data || []).map((v) => ({
          ...v,
          category: (v.category || '').trim().toLowerCase(),
          thumbnail: v.thumbnail || youtubeThumb(v.youtubeId),
          views: v.views || '0',
          likes: v.likes || '',
          duration: v.duration || '',
          description: (v.description || '').trim() || null,
        })));
      }
      if (pRes.success) {
        setPlaylistsData((pRes.data || []).map((p) => {
          const name = p.name || p.title || '';
          const videoCount = parseInt(p.videoCount != null ? p.videoCount : p.views, 10) || 0;
          // Auto-generate thumbnail: prefer stored thumbnail URL, then use cover youtubeId if set
          const thumbnail = p.thumbnail || youtubeThumb(p.youtubeId);
          return {
            ...p,
            name,
            title: name,
            category: (p.category || '').trim().toLowerCase(),
            videoCount,
            thumbnail,
            playlistUrl: p.playlistUrl || '',
            playlistViews: (p.playlistViews || '').trim() || null,
            description: (p.description || '').trim() || null,
          };
        }));
      }
      if (!vRes.success || !pRes.success) setKathaError(t('kathaPage.load_error_partial'));
    })      .catch(() => setKathaError(t('kathaPage.load_error')))
      .finally(() => { setKathaLoading(false); setKathaLoaded(true); });
  }, [t]);

  useEffect(() => {
    homeApi
      .getAll()
      .then((res) => {
        if (!res.success || !res.data) return;
        const yt = res.data.social_youtube;
        if (!yt || typeof yt !== 'object') return;
        if ((yt.url || '').trim()) setYtUrl(String(yt.url).trim());
        const statsIn = Array.isArray(yt.stats) ? yt.stats : [];
        const labelKeys = [
          'kathaPage.stat_subscribers',
          'kathaPage.stat_videos',
          'kathaPage.stat_views',
        ];
        setYtStats(
          labelKeys.map((labelKey, idx) => {
            const fromArr = statsIn[idx] || {};
            const fallbackValue = DEFAULT_YT_VALUES[idx];
            return {
              value: (fromArr.value != null ? String(fromArr.value) : null)
                || (yt[`stat${idx + 1}Value`] != null ? String(yt[`stat${idx + 1}Value`]) : fallbackValue),
              // Prefer i18n for default English labels; keep custom admin labels
              labelKey,
              customLabel: (fromArr.label != null ? String(fromArr.label) : null)
                || (yt[`stat${idx + 1}Label`] != null ? String(yt[`stat${idx + 1}Label`]) : null),
            };
          })
        );
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  const videosScrollRef = useRef(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const didDrag = useRef(false);

  const videoLabels = useMemo(() => ({
    katha: t('kathaPage.filters.katha'),
    pravachan: t('kathaPage.filters.pravachan'),
    bhajan: t('kathaPage.filters.bhajan'),
    live: t('kathaPage.filters.live'),
    shorts: t('kathaPage.filters.shorts'),
    meditation: t('kathaPage.filters.meditation'),
  }), [t]);

  const playlistLabels = useMemo(() => ({
    katha: t('kathaPage.filters.katha_series'),
    pravachan: t('kathaPage.filters.pravachan_series'),
    bhajan: t('kathaPage.filters.bhajan_collections'),
    live: t('kathaPage.filters.live'),
    shorts: t('kathaPage.filters.shorts'),
    meditation: t('kathaPage.filters.meditation'),
  }), [t]);

  // Category chips derived from categories actually present in loaded data
  const videoCategoryFilters = useMemo(
    () => buildCategoryFilters(topVideosData, {
      allLabel: t('kathaPage.all_videos'),
      labels: videoLabels,
    }),
    [topVideosData, t, videoLabels]
  );

  const playlistCategoryFilters = useMemo(
    () => buildCategoryFilters(playlistsData, {
      allLabel: t('kathaPage.all_collections'),
      labels: playlistLabels,
    }),
    [playlistsData, t, playlistLabels]
  );

  // If active filter no longer exists in data, reset to "all"
  useEffect(() => {
    if (videoFilter !== 'all' && !videoCategoryFilters.some((f) => f.val === videoFilter)) {
      setVideoFilter('all');
    }
  }, [videoFilter, videoCategoryFilters]);

  useEffect(() => {
    if (playlistFilter !== 'all' && !playlistCategoryFilters.some((f) => f.val === playlistFilter)) {
      setPlaylistFilter('all');
    }
  }, [playlistFilter, playlistCategoryFilters]);

  const filteredVideos = useMemo(() => {
    return topVideosData.filter(v => videoFilter === 'all' || v.category === videoFilter);
  }, [videoFilter, topVideosData]);

  const filteredPlaylists = useMemo(() => {
    return playlistsData.filter(p => playlistFilter === 'all' || p.category === playlistFilter);
  }, [playlistFilter, playlistsData]);

  const localizedMeta = useCallback((category) => {
    const meta = getCategoryMeta(category);
    const key = `kathaPage.tag.${category}`;
    const name = t(key);
    return {
      ...meta,
      name: name !== key ? name : meta.name,
    };
  }, [t]);

  const handleWatchVideo = (video) => {
    if (didDrag.current) return;
    setPlayingVideo(video);
  };

  const closeVideo = useCallback(() => {
    setPlayingVideo(null);
  }, []);

  const handleWatchPlaylist = (playlist) => {
    openSocialUrl(playlist.playlistUrl);
  };

  // Desktop: only convert clearly-horizontal wheel gestures to list scroll (don't block page scroll)
  useEffect(() => {
    const el = videosScrollRef.current;
    if (!el) return;

    const onWheel = (e) => {
      // Let vertical page scroll pass through; only handle horizontal intent
      if (Math.abs(e.deltaY) >= Math.abs(e.deltaX)) return;
      const canScroll = el.scrollWidth > el.clientWidth;
      if (!canScroll) return;
      e.preventDefault();
      el.scrollLeft += e.deltaX;
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const handleMouseDown = useCallback((e) => {
    const el = videosScrollRef.current;
    if (!el) return;
    isDragging.current = true;
    didDrag.current = false;
    dragStartX.current = e.clientX;
    dragScrollLeft.current = el.scrollLeft;
    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current) return;
    const el = videosScrollRef.current;
    if (!el) return;
    e.preventDefault();
    const walk = e.clientX - dragStartX.current;
    if (Math.abs(walk) > 5) didDrag.current = true;
    el.scrollLeft = dragScrollLeft.current - walk;
  }, []);

  const handleMouseUp = useCallback(() => {
    const el = videosScrollRef.current;
    isDragging.current = false;
    if (el) {
      el.style.cursor = 'grab';
      el.style.userSelect = '';
    }
  }, []);

  const defaultEnglishLabels = ['Subscribers', 'Videos', 'Views'];

  const videoJsonLd = useMemo(() => {
    if (!topVideosData.length) return null;
    const videos = topVideosData.slice(0, 10).map(v => ({
      "@type": "VideoObject",
      "name": v.title || "Katha by Shree Radhe Krishna Bapu",
      "description": v.description || `Spiritual discourse by Pujya Mahant Shree Radhe Krishna Bapu — Shree Ram Ranuja Dham Ashram.`,
      "thumbnailUrl": v.thumbnail || (v.videoId ? `https://img.youtube.com/vi/${v.videoId}/hqdefault.jpg` : undefined),
      "uploadDate": v.publishedAt || v.date || undefined,
      "embedUrl": v.videoId ? `https://www.youtube.com/embed/${v.videoId}` : undefined,
      "contentUrl": v.videoId ? `https://www.youtube.com/watch?v=${v.videoId}` : undefined,
      "author": {
        "@type": "Person",
        "name": "Shree Radhe Krishna Bapu",
        "@id": "https://www.shreeramranujadham.org/#bapu"
      },
      "publisher": {
        "@id": "https://www.shreeramranujadham.org/#organization"
      }
    }));
    return { "@context": "https://schema.org", "@graph": videos };
  }, [topVideosData]);

  return (
    <>
      <Helmet>
        <title>{t('kathaPage.meta.title')}</title>
        <meta name="description" content={t('kathaPage.meta.description')} />
        <link rel="canonical" href="https://www.shreeramranujadham.org/katha" />
        <meta property="og:title" content={t('kathaPage.meta.title')} />
        <meta property="og:description" content={t('kathaPage.meta.description')} />
        <meta property="og:url" content="https://www.shreeramranujadham.org/katha" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.shreeramranujadham.org/logo.jpg" />
        {videoJsonLd && (
          <script type="application/ld+json">{JSON.stringify(videoJsonLd)}</script>
        )}
      </Helmet>

      {/* ── Trending Divine Videos ─────────────────────── */}
      <section className="pt-24 md:pt-28 pb-6 md:pb-8 bg-white relative">
        <div className="container mx-auto px-4 relative z-10">
          {kathaLoaded && topVideosData.length > 0 && (
            <SectionHeading title={t('kathaPage.trending_title')}>
              <CategoryFilters filters={videoCategoryFilters} active={videoFilter} onSelect={setVideoFilter} />
            </SectionHeading>
          )}

          {/* Videos Horizontal Scroll — wheel + drag supported */}
          {kathaLoaded && topVideosData.length > 0 && (
          <div
            ref={videosScrollRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            className="overflow-x-auto overflow-y-hidden scrollbar-hide -mx-4 px-4 pb-2 md:pb-4 cursor-grab"
            style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y' }}
          >
            <div className="flex flex-nowrap items-stretch gap-4 md:gap-6" style={{ width: 'max-content' }}>
              {filteredVideos.map(video => {
                const meta = localizedMeta(video.category);
                return (
                  <MediaCard
                    key={video.id}
                    meta={meta}
                    thumbnail={video.thumbnail}
                    title={video.title}
                    onClick={() => handleWatchVideo(video)}
                    widthClassName="flex-shrink-0 w-72 md:w-80"
                    imageInteractive={false}
                    overlayIcon="fas fa-play ml-0.5"
                    bottomLeftText={video.duration}
                    stats={[
                      { icon: 'fas fa-eye', text: t('kathaPage.views', { count: video.views }), accent: true },
                      ...(video.likes ? [{ icon: 'fas fa-heart', text: t('kathaPage.likes', { count: video.likes }), className: 'text-red-500' }] : []),
                    ]}
                    description={video.description || null}
                    button={{ icon: 'fas fa-play', label: t('kathaPage.watch_video'), className: 'py-3 rounded-xl font-semibold text-sm' }}
                  />
                );
              })}
            </div>
          </div>
          )}

          {/* CTA Box */}
          <div className="text-center mt-4 md:mt-12">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-orange-100 max-w-xl mx-auto">
              <h3 className="text-lg font-bold text-gray-800 mb-3">
                {t('kathaPage.cta_title')}
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                {t('kathaPage.cta_body')}
              </p>
              <SocialLink
                href={ytUrl}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold text-base rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <i className="fab fa-youtube mr-2 text-lg"></i>
                {t('kathaPage.subscribe_now')}
              </SocialLink>
            </div>
          </div>
        </div>
      </section>

      {/* ── Spiritual Playlists — hidden until loaded and has data ── */}
      {kathaLoaded && playlistsData.length > 0 && (
      <section className="pt-6 md:pt-8 pb-6 md:pb-8 bg-gray-50 relative overflow-hidden" id="featured-playlist">
        <div className="container mx-auto px-4 relative z-10">
          <SectionHeading title={t('kathaPage.playlists_title')}>
            <CategoryFilters filters={playlistCategoryFilters} active={playlistFilter} onSelect={setPlaylistFilter} />
          </SectionHeading>

          {/* Playlists Grid — same container width/margins as trending so cards are wider */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 items-stretch">
            {filteredPlaylists.map((playlist) => {
              const meta = localizedMeta(playlist.category);
              const estHours = Math.round((playlist.videoCount * 30) / 60);

              return (
                <MediaCard
                  key={playlist.id}
                  meta={meta}
                  thumbnail={playlist.thumbnail}
                  title={playlist.name}
                  onClick={() => handleWatchPlaylist(playlist)}
                  overlayIcon="fab fa-youtube"
                  imageAspect="16/9"
                  stats={[
                    { icon: 'fas fa-play-circle', text: t('kathaPage.videos_count', { count: playlist.videoCount }), accent: true },
                    ...(playlist.playlistViews ? [{ icon: 'fas fa-users', text: t('kathaPage.playlist_views', { count: playlist.playlistViews }), accent: true }] : []),
                  ]}
                  description={playlist.description || null}
                  button={{ icon: 'fab fa-youtube text-lg', label: t('kathaPage.watch_playlist'), className: 'py-3.5 rounded-2xl font-bold' }}
                />
              );
            })}
          </div>

        </div>
      </section>
      )}

      {/* ── YouTube Subscribe CTA Section ──────────────── */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-red-600 via-pink-600 to-purple-700 relative overflow-hidden" style={{ marginTop: '10px' }}>
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-24 h-24 bg-white rounded-full opacity-10 animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-32 h-32 bg-yellow-300 rounded-full opacity-15 animate-bounce"></div>
          <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-red-300 rounded-full opacity-20 animate-pulse"></div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <div className="inline-flex items-center bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-6 py-3 mb-4">
                <i className="fab fa-youtube text-red-300 mr-3 text-2xl"></i>
                <span className="text-white font-semibold">{t('kathaPage.community_badge')}</span>
              </div>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {t('kathaPage.subscribe_title_1')}<br />
              <span className="text-yellow-300">{t('kathaPage.subscribe_title_2')}</span>
            </h2>

            <p className="text-lg md:text-xl text-white opacity-90 mb-8 leading-relaxed">
              {t('kathaPage.subscribe_body')}
            </p>

            {/* Channel Stats — from Social Media → YouTube */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
              {ytStats.map((stat, idx) => {
                const label = (stat.customLabel
                  && !defaultEnglishLabels.includes(stat.customLabel))
                  ? stat.customLabel
                  : t(stat.labelKey);
                return (
                  <div key={stat.labelKey || idx} className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-4">
                    <div className="text-2xl md:text-3xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-sm text-white opacity-80">{label}</div>
                  </div>
                );
              })}
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-4">
                <div className="text-2xl md:text-3xl font-bold text-white mb-1">{t('kathaPage.stat_daily')}</div>
                <div className="text-sm text-white opacity-80">{t('kathaPage.stat_uploads')}</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <SocialLink
                href={ytUrl}
                className="inline-flex items-center justify-center px-8 py-4 bg-red-600 text-white font-bold text-lg rounded-full hover:bg-red-700 transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:-translate-y-1"
              >
                <i className="fab fa-youtube mr-3 text-2xl"></i>
                {t('kathaPage.subscribe_yt')}
              </SocialLink>
              <SocialLink
                href={ytUrl}
                className="inline-flex items-center justify-center px-8 py-4 bg-white bg-opacity-20 backdrop-blur-sm text-white font-bold text-lg rounded-full hover:bg-opacity-30 transition-all duration-300 border-2 border-white border-opacity-30"
              >
                <i className="fas fa-bell mr-3"></i>
                {t('kathaPage.get_notifications')}
              </SocialLink>
            </div>

            {/* Social Proof */}
            <div className="mt-8 text-white opacity-80">
              <p className="text-sm">
                {t('kathaPage.social_proof')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Video Lightbox ──────────────────────────── */}
      <VideoModal video={playingVideo} onClose={closeVideo} />
    </>
  );
};

export default Katha;
